import { taskAdminApis } from '@gleamer/apis';
import { TaskAdmin } from '@gleamer/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { ADMIN_TASKS_KEY } from './useAdminTasks';
import { useAuthorization } from './useImports';

const {
  fetchComputationIssues,
  fetchPredictionImport,
  fetchTaskComputations,
  fetchTaskPredictionImports,
  importPredictionsAgain,
  settlePredictionImport,
  stopTaskComputation,
} = taskAdminApis;

type PredictionImportAction = TaskAdmin.PredictionImportAction;
type PredictionImportStatus = TaskAdmin.PredictionImportStatus;

/** While a task's items compute or its predictions import, the drawer reads them again this often. */
export const TASK_ACTIVITY_POLL_MS = 5_000;

/** How many computations the drawer lists: the latest and the ones before it. */
export const COMPUTATIONS_LIMIT = 10;

export const TASK_COMPUTATIONS_KEY = 'task-computations';
export const COMPUTATION_ISSUES_KEY = 'task-computation-issues';
export const PREDICTION_IMPORTS_KEY = 'task-prediction-imports';
export const PREDICTION_IMPORT_KEY = 'prediction-import';

export function isImportInFlight(status: PredictionImportStatus): boolean {
  return status === 'REQUESTED' || status === 'IMPORTING';
}

/**
 * Called with whether a polled answer is still in motion: when it stops being, the tasks list and the task's
 * details are read again, since the task's status follows its computations and its imports.
 */
function useSettledRefresh() {
  const queryClient = useQueryClient();
  const wasLive = useRef(false);
  return (live: boolean) => {
    if (wasLive.current && !live) {
      queryClient.invalidateQueries({ queryKey: [ADMIN_TASKS_KEY] });
    }
    wasLive.current = live;
  };
}

/**
 * Stops a computation that will not finish (update:labelTask); then reads the computations and the task again, which
 * is no longer held from its labellers.
 */
export function useStopComputation(taskId: number) {
  const auth = useAuthorization();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (computationId: number) => stopTaskComputation(auth, computationId),
    onSuccess: () => {
      [TASK_COMPUTATIONS_KEY, ADMIN_TASKS_KEY].forEach(key =>
        queryClient.invalidateQueries({ queryKey: [key] })
      );
      queryClient.invalidateQueries({ queryKey: [TASK_COMPUTATIONS_KEY, taskId] });
    },
  });
}

/** The computations of a task's items, newest first; read again every 5 s while the latest runs. */
export function useTaskComputations(taskId: number) {
  const auth = useAuthorization();
  const queryClient = useQueryClient();
  const settled = useSettledRefresh();
  return useQuery({
    queryKey: [TASK_COMPUTATIONS_KEY, taskId],
    queryFn: () => fetchTaskComputations(auth, taskId, COMPUTATIONS_LIMIT),
    refetchInterval: data =>
      data?.[0]?.status === 'RUNNING' ? TASK_ACTIVITY_POLL_MS : false,
    onSuccess: data => {
      const running = data[0]?.status === 'RUNNING';
      settled(running);
      if (running) {
        // the progress (settledItems) is on the task's summary: read with each poll
        queryClient.invalidateQueries({
          queryKey: [ADMIN_TASKS_KEY, taskId, 'details'],
        });
      }
    },
  });
}

/** A page of a computation's issues; read again with the computation while it runs (`live`). */
export function useComputationIssues(
  computationId: number,
  page: { limit: number; offset: number },
  live: boolean
) {
  const auth = useAuthorization();
  return useQuery({
    queryKey: [COMPUTATION_ISSUES_KEY, computationId, page],
    queryFn: () => fetchComputationIssues(auth, computationId, page),
    keepPreviousData: true,
    refetchInterval: live ? TASK_ACTIVITY_POLL_MS : false,
  });
}

/** A task's prediction imports, newest first; read again every 5 s while one is requested or importing. */
export function useTaskPredictionImports(taskId: number, enabled = true) {
  const auth = useAuthorization();
  const settled = useSettledRefresh();
  return useQuery({
    queryKey: [PREDICTION_IMPORTS_KEY, taskId],
    queryFn: () => fetchTaskPredictionImports(auth, taskId),
    enabled,
    refetchInterval: data =>
      data?.some(i => isImportInFlight(i.status))
        ? TASK_ACTIVITY_POLL_MS
        : false,
    onSuccess: data => settled(data.some(i => isImportInFlight(i.status))),
  });
}

/** One import with a page of its skipped and failed patients; read again while it is in flight (`live`). */
export function usePredictionImportIssues(
  importId: number,
  page: { limit: number; offset: number },
  live: boolean
) {
  const auth = useAuthorization();
  return useQuery({
    queryKey: [PREDICTION_IMPORT_KEY, importId, page],
    queryFn: () => fetchPredictionImport(auth, importId, page),
    keepPreviousData: true,
    refetchInterval: live ? TASK_ACTIVITY_POLL_MS : false,
  });
}

export type PredictionImportCommand =
  | { type: 'import-again' }
  | { type: PredictionImportAction; importId: number };

/**
 * Imports the task's predictions again, or replays, accepts or abandons one import; then reads the imports, the
 * import's issues and the task again (its status follows its imports).
 */
export function usePredictionImportCommand(taskId: number) {
  const auth = useAuthorization();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: PredictionImportCommand) =>
      command.type === 'import-again'
        ? importPredictionsAgain(auth, taskId)
        : settlePredictionImport(auth, command.importId, command.type),
    onSuccess: () => {
      [PREDICTION_IMPORTS_KEY, PREDICTION_IMPORT_KEY, ADMIN_TASKS_KEY].forEach(
        key => queryClient.invalidateQueries({ queryKey: [key] })
      );
    },
  });
}
